import { FileActionButton } from "../file-action/file-action-controls";
import React, { useEffect, useRef, useState, CSSProperties } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { useNavigate } from "react-router-dom";
import RouteConstant from "../../constants/route.constants";
import { Column, ColumnEditorOptions } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import ClipLoader from "react-spinners/ClipLoader";
import vendorService from "../../services/vendors/vendor.service";
import { UpdateVendorDTO } from "../../models/vendors/vendor-model";
import  {VendorSummaryDTO}  from "../../models/vendors/vendor-model";
import AddVendor from "./AddVendor";
import { exportVendorsToExcel } from "./vendor-export";


const VendorDataTable: React.FC = () => {
  const [vendors, setVendors] = useState<VendorSummaryDTO[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<VendorSummaryDTO[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<VendorSummaryDTO | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const toast = useRef<Toast>(null);
const navigate = useNavigate();

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const data = await vendorService.getAllVendors();
      setVendors(data);
      applyFilters(data, searchQuery);
    } catch (error) {
      console.error("Failed to fetch vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters dynamically across all fields
  const applyFilters = (data: VendorSummaryDTO[], query: string) => {
    if (!query.trim()) {
      setFilteredVendors(data);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = data.filter((vendor) => {
      return (
        vendor.firmInfo?.toLowerCase().includes(lowerQuery) ||
        vendor.userName?.toLowerCase().includes(lowerQuery) ||
        vendor.officeContactNbr?.toLowerCase().includes(lowerQuery) ||
        vendor.address?.toLowerCase().includes(lowerQuery) ||
        vendor.contactPerson1_Name?.toLowerCase().includes(lowerQuery) ||
        vendor.contactPerson1_Nbr?.toLowerCase().includes(lowerQuery)
      );
    });
    setFilteredVendors(filtered);
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // Update filtered list whenever search query changes
  useEffect(() => {
    applyFilters(vendors, searchQuery);
  }, [searchQuery, vendors]);

  const showMessage = (summary: string, detail: string, severity: "success" | "error") => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleExport = async () => {
    if (!filteredVendors || filteredVendors.length === 0) {
      showMessage("Warning", "No data to export", "error");
      return;
    }

    setExportLoading(true);
    try {
      exportVendorsToExcel(filteredVendors);
      showMessage("Success", "Vendors exported successfully", "success");
    } catch (error) {
      console.error("Export error:", error);
      showMessage("Error", "Failed to export vendors", "error");
    } finally {
      setExportLoading(false);
    }
  };

 const onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
  const { newData, index } = e;
  const originalData = vendors[index];

  const updatePayload: UpdateVendorDTO = {
    vendorID: originalData.vendorID,
    firmInfo: newData.firmInfo,
    additionalInfo: newData.additionalInfo || null,
    officeContactNbr: newData.officeContactNbr || null,
    email: newData.email || null,
    address: newData.address || null,
    contactPerson1_Name: newData.contactPerson1_Name || null,
    contactPerson1_Nbr: newData.contactPerson1_Nbr || null,
    contactPerson2_Name: newData.contactPerson2_Name || null,
    contactPerson2_Nbr: newData.contactPerson2_Nbr || null,
    imagePath: newData.imagePath || null,
    isDeleted: originalData.isDeleted, 
    modifiedBy: originalData.createdBy, 
    userID: originalData.createdBy      
  };

  try {
    await vendorService.updateVendor(updatePayload);
    showMessage("Success", "Vendor updated successfully", "success");

    const updatedList = [...vendors];
    updatedList[index] = { ...originalData, ...newData };
    setVendors(updatedList);
  } catch (error) {
    showMessage("Error", "Failed to update vendor", "error");
    console.error("Update error:", error);
  }
};

  const confirmDelete = (vendor: VendorSummaryDTO) => {
    setSelectedVendor(vendor);
    setShowDeleteDialog(true);
  };

  const deleteVendor = async () => {
    if (!selectedVendor) return;

    try {
      await vendorService.deleteVendor(selectedVendor.vendorID);
      showMessage("Success", "Vendor deleted", "success");
      fetchVendors();
    } catch (error) {
      showMessage("Error", "Failed to delete vendor", "error");
      console.error("Delete error:", error);
    } finally {
      setSelectedVendor(null);
      setShowDeleteDialog(false);
    }
  };

const textEditor = (field: keyof VendorSummaryDTO) => (options: ColumnEditorOptions) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    let isValid = true;

    
    if (field === "contactPerson1_Name") {
      isValid = /^[A-Za-z\s]*$/.test(value);
    }

    if (field === "officeContactNbr" || field === "contactPerson1_Nbr") {
      isValid = /^\d*$/.test(value);
    }

    if (isValid) {
      options.editorCallback?.(value);
    }
  };

  return (
    <input
      type="text"
      className="p-inputtext p-component w-full"
      value={options.value || ""}
      onChange={handleChange}
    />
  );
};

  const deleteTemplate = (rowData: VendorSummaryDTO) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger"
      onClick={() => confirmDelete(rowData)}
      tooltip="Delete Vendor"
    />
  );

  const loaderStyle: CSSProperties = {
    display: "block",
    margin: "0 auto",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };

  return (
    <>
     <div className="max-w-5xl mx-auto px-5 py-2">

      {loading ? (
        <ClipLoader cssOverride={loaderStyle} size={50} color="#90ee90" loading={loading} />
      ) : (
        <>
          <Toast ref={toast} />

          <div className="col-12">
            <div className="card p-4">
              <div className="flex flex-column gap-3">
                {/* Header Section with Info and Filter */}
                <div className="card p-4 w-full" style={{
                  backgroundColor: '#e8f5e9',
                  borderRadius: '12px'
                }}>
                  {/* Header Section with gradient - nested inside outer container */}
                  <div className="flex align-items-center gap-3 mb-4" style={{
                    backgroundImage: 'linear-gradient(to right, white, rgb(232, 245, 233))',
                    padding: '16px',
                    borderRadius: '8px',
                    marginLeft: '-16px',
                    marginRight: '-16px',
                    marginTop: '-16px',
                    paddingLeft: '16px',
                    paddingRight: '16px'
                  }}>
                    <div style={{
                      backgroundColor: 'rgb(76, 175, 80)',
                      borderRadius: '10px',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '48px',
                      height: '48px'
                    }}>
                      <i className="pi pi-users" style={{ color: 'white', fontSize: '1.5rem' }}></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h5 className="m-0 mb-1" style={{ color: '#2d3748', fontWeight: '600', fontSize: '1.125rem' }}>
                        Add Vendors
                      </h5>
                      <p className="m-0" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        Track, manage, and review your vendor information
                      </p>
                      {/* Vendor Count */}
                      <div className="flex align-items-center gap-2 mt-2">
                        <i className="pi pi-chart-bar" style={{ fontSize: '0.875rem', color: 'rgb(76, 175, 80)' }}></i>
                        <span style={{ fontSize: '0.875rem', color: '#2d3748' }}>
                          <span className="font-bold">{vendors.length}</span> Total vendors 
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Filter Header */}
                  <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-filter" style={{ color: 'rgb(76, 175, 80)', fontSize: '1.1rem' }}></i>
                    <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.95rem' }}>Filter Vendors</span>
                  </div>

                  {/* Filter Section */}
                  <div className="flex justify-content-between align-items-center gap-3">
                    <div className="flex align-items-center gap-2" style={{ flex: 1, maxWidth: '400px' }}>
                      <span className="p-input-icon-left w-full">
                        <i className="pi pi-search" />
                        <input
                          type="search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Keyword Search..."
                          className="p-inputtext p-component w-full"
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            padding: '0.6rem 0.75rem 0.6rem 2.5rem'
                          }}
                        />
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex align-items-center gap-2">
                      <FileActionButton action="export"
                        label="Export to Excel"
                        icon="pi pi-file-excel"
                        className="p-button-success"
                        onClick={handleExport}
                        loading={exportLoading}
                        disabled={filteredVendors.length === 0}
                        
                      />
                  <Button
                    label="Add Vendor"
                    icon="pi pi-plus"
                    className="p-button-success"
                    onClick={() => setShowAddDialog(true)}
                  />
                    </div>
                    </div>
                  </div>
                </div>

              {/* Data Table Card */}
              <div style={{ marginTop: '1.5rem' }}>
                <DataTable
                  value={filteredVendors}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  emptyMessage="No vendors found"
                  stripedRows
                  editMode="row"
                  onRowEditComplete={onRowEditComplete}
                  dataKey="vendorID"
                  responsiveLayout="scroll"
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  className="rounded-datatable"
                  style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: 'white',
                    border: '1px solid rgb(224, 224, 224)'
                  }}
                  rowHover
                >
                  <Column 
                    field="vendorID" 
                    header="VENDOR ID" 
                    style={{ width: "10%" }}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    field="userName" 
                    header="USER NAME"
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    field="firmInfo" 
                    header="FIRM INFO" 
                    editor={textEditor("firmInfo")}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    field="officeContactNbr" 
                    header="OFFICE CONTACT" 
                    editor={textEditor("officeContactNbr")}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    field="address" 
                    header="ADDRESS" 
                    editor={textEditor("address")}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    field="email" 
                    header="EMAIL"
                    editor={textEditor("email")}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    field="contactPerson1_Name" 
                    header="PERSON NAME" 
                    editor={textEditor("contactPerson1_Name")}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    field="contactPerson1_Nbr" 
                    header="PERSON NUMBER" 
                    editor={textEditor("contactPerson1_Nbr")}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    field="additionalInfo" 
                    header="INFO" 
                    editor={textEditor("additionalInfo")}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                  <Column 
                    rowEditor 
                    header="EDIT" 
                    style={{ width: "8%" }}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0',
                      textAlign: 'center'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem',
                      textAlign: 'center'
                    }}
                  />
                  <Column 
                    body={deleteTemplate} 
                    header="DELETE" 
                    style={{ width: "8%" }}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0',
                      textAlign: 'center'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem',
                      textAlign: 'center'
                    }}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </>
      )}

<Dialog
  visible={showDeleteDialog}
  header="Confirm Delete"
  modal
  onHide={() => setShowDeleteDialog(false)}
  footer={
    <div className="flex justify-content-end gap-2">
      <Button label="No" icon="pi pi-times" onClick={() => setShowDeleteDialog(false)} className="p-button-text" />
      <Button label="Yes" icon="pi pi-check" onClick={deleteVendor} className="p-button-danger" />
    </div>
  }
>
  <p>
    Are you sure you want to delete vendor{" "}
    <strong>{selectedVendor?.firmInfo || selectedVendor?.userName}</strong>?
  </p>
</Dialog>

<Dialog
  visible={showAddDialog}
  header="Add Vendor"
  className="quickview w-50"
  breakpoints={{ "960px": "75vw", "641px": "90vw" }}
  modal
  onHide={() => setShowAddDialog(false)}
>
  <AddVendor
    onSuccess={() => {
      setShowAddDialog(false);
      fetchVendors(); 
    }}
  />
</Dialog>
  </div>
    </>
  );
};

export default VendorDataTable;
